import { useState } from 'react';

import { isApiError } from '@platform/api-client';
import type { AdminIntegration } from '@platform/contracts';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  SchemaForm,
  schemaIssuesToFieldErrors,
  schemaStoredChoice,
  schemaSubmittedValues,
  schemaVisibleValues,
  type SchemaValues,
  useToast,
  validateSchemaValues,
} from '@platform/ui';

import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

import { StepUpPrompt, useStepUp } from './step-up-prompt';

/**
 * The Credentials tab (MASTER_PLAN M3.9, FR-MKT-03, SECURITY.md §7.4).
 *
 * The form is generated from the catalog's `credentialSchema`, so adding an integration
 * is adding a catalog entry and never a screen. Three rules it exists to hold:
 *
 * 1. **A secret is never prefilled and never read back.** The API has no field that could
 *    return one. The boxes start empty, the `credHint` mask sits under each one so the
 *    admin can tell which key is installed, and on a rotation a field left blank keeps
 *    what is stored.
 * 2. **The typed value lives here and dies here.** It is component state, never a query
 *    cache entry, never storage, and it is cleared the moment the save succeeds.
 * 3. **Saving is a step-up route.** `403 MFA_STEP_UP_REQUIRED` opens the prompt and the
 *    save is re-run after the code is accepted — the admin presses save once.
 *
 * Saving also runs `testConnection()` on the server, which is why the confirmation can
 * say whether the credentials actually work instead of only that they were stored.
 */

export interface CredentialsFormProps {
  entry: AdminIntegration;
  /** Refetch the install after a successful save or a probe. */
  onChanged: () => void;
}

export function CredentialsForm({ entry, onChanged }: CredentialsFormProps) {
  const client = useIntegrationsClient();
  const { toast } = useToast();
  const stepUp = useStepUp();

  const schema = entry.integration.credentialSchema;
  const install = entry.install;
  const rotating = install?.credentialsSet === true;

  // An update opens on the kind that is stored, so the admin sees the fields it uses.
  const [values, setValues] = useState<SchemaValues>(() =>
    schemaStoredChoice(schema, install?.credHint ?? {}),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  if (install === null) {
    return (
      <Alert tone="info" title="This integration is not enabled for your clinic.">
        Ask us to turn it on and the credential form appears here.
      </Alert>
    );
  }

  const save = async (): Promise<void> => {
    const local = validateSchemaValues(schema, values, { omitEmptySecrets: rotating });
    setErrors(local);
    setFormError(undefined);
    if (Object.keys(local).length > 0) return;

    const credentials = scalarsOnly(schemaSubmittedValues(schemaVisibleValues(schema, values)));
    setBusy(true);
    try {
      await stepUp.run(
        rotating ? 'Updating credentials' : 'Connecting this integration',
        async () => {
          const result = await client.putCredentials(entry.integration.key, { credentials });
          /* The secret leaves the browser here and is dropped on the same tick. */
          setValues(schemaStoredChoice(schema, credentials));
          toast({
            tone: result.health.result === 'failed' ? 'error' : 'success',
            message:
              result.health.result === 'failed'
                ? 'Saved, but the vendor refused the connection. Check the values and try again.'
                : 'Saved. The connection test passed.',
          });
          onChanged();
        },
      );
    } catch (error) {
      if (isApiError(error) && error.code === 'VALIDATION_FAILED') {
        const mapped = schemaIssuesToFieldErrors(schema, error.issues);
        const { '': formLevel, ...fields } = mapped;
        setErrors(fields);
        setFormError(formLevel ?? error.message);
      } else {
        setFormError(isApiError(error) ? error.message : 'That could not be saved. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          as="h2"
          title={rotating ? 'Update credentials' : 'Connect this integration'}
          subtitle={
            rotating
              ? 'Leave a field empty to keep the key that is already stored.'
              : 'Paste the keys from your own vendor account. They are encrypted for this clinic and never shown again.'
          }
        />
        <form
          className="px-6 pt-5 pb-6 max-[480px]:px-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {formError === undefined ? null : (
            <Alert tone="error" className="mb-5">
              {formError}
            </Alert>
          )}

          <SchemaForm
            schema={schema}
            values={values}
            onChange={(name, value) => {
              setValues((current) => ({ ...current, [name]: value }));
              setErrors((current) => {
                const { [name]: _removed, ...rest } = current;
                return rest;
              });
            }}
            errors={errors}
            hints={install.credHint}
            disabled={busy}
            idPrefix="credentials"
          />

          <p className="mt-5 text-xs text-neutral-400-text">
            We store these encrypted with a key held for your clinic alone. Nobody at Object Labs
            can read them back, and neither can this page.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {/* `type="button"` with an explicit handler, not a submit button: the form's
                own `onSubmit` still handles Enter inside a field, and a real browser does
                not double-fire because a non-submit button never submits. */}
            <Button
              disabled={busy}
              onClick={() => {
                void save();
              }}
            >
              {busy ? 'Saving…' : rotating ? 'Save and test' : 'Connect and test'}
            </Button>
            {rotating && install.credUpdatedBy !== null ? (
              <span className="text-sm text-neutral-500">
                Last changed by {install.credUpdatedBy}.
              </span>
            ) : null}
          </div>
        </form>
      </Card>

      <StepUpPrompt request={stepUp.pending} onClose={stepUp.cancel} />
    </>
  );
}

/**
 * `PutCredentialsRequest` admits strings, numbers and booleans and nothing else: a
 * credential is a key, not a list. An array-typed credential property would be a catalog
 * authoring mistake, and dropping it here means the request is still well formed when the
 * server refuses it.
 */
function scalarsOnly(values: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [name, value] of Object.entries(values)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[name] = value;
    }
  }
  return out;
}
